import React, { useState, useRef } from 'react';
import { DictionaryType, DictionaryMetadata } from '../../types';
import { X, Upload, FileText, AlertTriangle, Loader2, StopCircle, CheckCircle2 } from 'lucide-react';
import { ValidationErrorItem } from '../../workers/dictionary.worker';
import { useToast } from '../../context/ToastContext';

interface ImportModalProps {
  dictType: DictionaryType;
  existingMetadata?: DictionaryMetadata | null;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

type MergeMode = 'merge' | 'replace' | 'add_missing';

export const ImportModal: React.FC<ImportModalProps> = ({
  dictType,
  existingMetadata,
  isOpen,
  onClose,
  onSuccess,
}) => {
  const toast = useToast();
  const hasExistingData = existingMetadata ? existingMetadata.wordCount > 0 : false;
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [mergeMode, setMergeMode] = useState<MergeMode>('merge');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [progressPercent, setProgressPercent] = useState<number>(0);

  // Validation errors & Skip option
  const [validationErrors, setValidationErrors] = useState<ValidationErrorItem[]>([]);
  const [canSkipErrors, setCanSkipErrors] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const workerRef = useRef<Worker | null>(null);

  if (!isOpen) return null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 50 * 1024 * 1024) {
        setErrorMessage('Dung lượng file vượt quá giới hạn 50MB.');
        setSelectedFile(null);
        return;
      }
      setSelectedFile(file);
      setErrorMessage('');
      setValidationErrors([]);
      setCanSkipErrors(false);
    }
  };

  const handleCancelImport = () => {
    if (workerRef.current) {
      workerRef.current.terminate();
      workerRef.current = null;
    }
    setIsProcessing(false);
    setStatusMessage('');
    setProgressPercent(0);
    setErrorMessage('Đã hủy quá trình nhập từ điển.');
  };

  const startImportWorker = async (skipErrors: boolean = false) => {
    if (!selectedFile) return;

    const effectiveMode = hasExistingData ? mergeMode : 'replace';

    try {
      setIsProcessing(true);
      setErrorMessage('');
      setValidationErrors([]);
      setCanSkipErrors(false);
      setProgressPercent(0);
      setStatusMessage('Đang đọc file...');

      const fileContent = await selectedFile.text();

      const worker = new Worker(
        new URL('../../workers/dictionary.worker.ts', import.meta.url),
        { type: 'module' }
      );
      workerRef.current = worker;

      worker.postMessage({
        type: 'START_FULL_IMPORT',
        dictType,
        content: fileContent,
        mergeMode: effectiveMode,
        hasExistingData,
        skipErrors,
      });

      worker.onmessage = (event) => {
        const data = event.data;

        if (data.type === 'IMPORT_PROGRESS') {
          setProgressPercent(data.percent);
          setStatusMessage(data.message);
        } else if (data.type === 'IMPORT_SUCCESS') {
          setProgressPercent(100);
          setStatusMessage('Nhập từ điển hoàn tất!');
          setIsProcessing(false);
          worker.terminate();
          workerRef.current = null;

          if (data.skippedLineCount && data.skippedLineCount > 0) {
            toast.info(`Đã nạp xong từ điển và bỏ qua ${data.skippedLineCount} dòng lỗi.`);
          } else {
            toast.success(`Đã nạp xong từ điển ${dictType}!`);
          }

          setTimeout(() => {
            onSuccess();
            onClose();
          }, 300);
        } else if (data.type === 'IMPORT_FAILED') {
          setIsProcessing(false);
          setValidationErrors(data.errors || []);
          setCanSkipErrors(!!data.canSkipErrors);
          setErrorMessage(data.message || 'Nhập từ điển thất bại.');
          worker.terminate();
          workerRef.current = null;
        }
      };

      worker.onerror = (err) => {
        setIsProcessing(false);
        setErrorMessage(`Lỗi Web Worker: ${err.message}`);
        worker.terminate();
        workerRef.current = null;
      };
    } catch (err: any) {
      setIsProcessing(false);
      setErrorMessage(err.message || 'Lỗi không xác định khi nạp từ điển.');
    }
  };

  const handleImportSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedFile) {
      setErrorMessage('Vui lòng chọn 1 file từ điển (.txt).');
      return;
    }
    startImportWorker(false);
  };

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1100,
        padding: '1rem',
      }}
    >
      <div
        style={{
          backgroundColor: '#ffffff',
          borderRadius: '0.75rem',
          maxWidth: '540px',
          width: '100%',
          maxHeight: '90vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            padding: '1rem 1.25rem',
            borderBottom: '1px solid var(--border-light)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}
        >
          <h3 style={{ fontSize: '1.125rem', fontWeight: 700, margin: 0 }}>
            Nhập file từ điển (<span style={{ color: 'var(--action-primary)' }}>{dictType}</span>)
          </h3>
          <button
            onClick={() => {
              if (isProcessing) handleCancelImport();
              onClose();
            }}
            style={{ color: 'var(--text-muted)' }}
          >
            <X size={20} />
          </button>
        </div>

        {/* Form Body */}
        <form onSubmit={handleImportSubmit} style={{ padding: '1.25rem', overflowY: 'auto' }}>
          {errorMessage && (
            <div
              style={{
                backgroundColor: '#fef2f2',
                color: 'var(--action-danger)',
                padding: '0.75rem',
                borderRadius: '0.375rem',
                fontSize: '0.8125rem',
                marginBottom: '1rem',
                border: '1px solid rgba(228, 59, 18, 0.2)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', fontWeight: 600, marginBottom: '0.25rem' }}>
                <AlertTriangle size={16} />
                <span>Cảnh báo định dạng file</span>
              </div>
              <p style={{ margin: 0, marginBottom: '0.5rem' }}>{errorMessage}</p>

              {/* Sample-check error list */}
              {validationErrors.length > 0 && (
                <div style={{ maxHeight: '120px', overflowY: 'auto', backgroundColor: '#ffffff', padding: '0.5rem', borderRadius: '0.25rem', border: '1px solid var(--border-light)', marginBottom: '0.75rem' }}>
                  {validationErrors.map((errItem, idx) => (
                    <div key={idx} style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: 'var(--text-main)', marginBottom: '0.25rem' }}>
                      <strong>Dòng {errItem.lineNum}:</strong> {errItem.lineContent}
                    </div>
                  ))}
                </div>
              )}

              {/* Skip Errors Quick Action Button */}
              {canSkipErrors && (
                <button
                  type="button"
                  onClick={() => startImportWorker(true)}
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    backgroundColor: '#10b981',
                    color: '#ffffff',
                    fontWeight: 600,
                    borderRadius: '0.375rem',
                    fontSize: '0.8125rem',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '0.375rem',
                  }}
                >
                  <CheckCircle2 size={16} />
                  <span>Bỏ qua {validationErrors.length} dòng lỗi này & Tiếp tục nhập</span>
                </button>
              )}
            </div>
          )}

          {/* File Selector */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.375rem' }}>
              Tải file .txt lên
            </label>
            <input
              type="file"
              accept=".txt"
              ref={fileInputRef}
              onChange={handleFileChange}
              disabled={isProcessing}
              style={{ display: 'none' }}
            />

            <button
              type="button"
              disabled={isProcessing}
              onClick={() => fileInputRef.current?.click()}
              style={{
                width: '100%',
                padding: '1.25rem',
                borderRadius: '0.5rem',
                border: '2px dashed var(--border-light)',
                backgroundColor: isProcessing ? '#f1f5f9' : '#f8fafc',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: '0.375rem',
                cursor: isProcessing ? 'not-allowed' : 'pointer',
              }}
            >
              <Upload size={24} style={{ color: 'var(--action-primary)' }} />
              <span style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--text-main)' }}>
                {selectedFile ? selectedFile.name : 'Bấm để chọn file .txt'}
              </span>
              {selectedFile && (
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  Dung lượng: {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
                </span>
              )}
            </button>
          </div>

          {/* Merge Mode Selector (Shown ONLY IF dictionary has existing data) */}
          {hasExistingData && (
            <div style={{ marginBottom: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: 600, marginBottom: '0.5rem' }}>
                Chọn Chế Độ Gộp Từ Điển:
              </label>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-light)',
                    backgroundColor: mergeMode === 'merge' ? 'var(--bg-card)' : '#ffffff',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="mergeMode"
                    value="merge"
                    disabled={isProcessing}
                    checked={mergeMode === 'merge'}
                    onChange={() => setMergeMode('merge')}
                    style={{ marginTop: '0.125rem' }}
                  />
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      1. Gộp từ điển (Mặc định) — Thêm từ chưa có. Nếu từ gốc đã tồn tại, đè nghĩa từ file mới lên nghĩa cũ.
                    </div>
                  </div>
                </label>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-light)',
                    backgroundColor: mergeMode === 'replace' ? 'var(--bg-card)' : '#ffffff',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="mergeMode"
                    value="replace"
                    disabled={isProcessing}
                    checked={mergeMode === 'replace'}
                    onChange={() => setMergeMode('replace')}
                    style={{ marginTop: '0.125rem' }}
                  />
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      2. Thay thế toàn bộ — Xóa toàn bộ dữ liệu cũ và ghi đè bằng dữ liệu từ file mới.
                    </div>
                  </div>
                </label>

                <label
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '0.5rem',
                    padding: '0.625rem 0.75rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-light)',
                    backgroundColor: mergeMode === 'add_missing' ? 'var(--bg-card)' : '#ffffff',
                    cursor: isProcessing ? 'not-allowed' : 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="mergeMode"
                    value="add_missing"
                    disabled={isProcessing}
                    checked={mergeMode === 'add_missing'}
                    onChange={() => setMergeMode('add_missing')}
                    style={{ marginTop: '0.125rem' }}
                  />
                  <div>
                    <div style={{ fontSize: '0.875rem', fontWeight: 600 }}>
                      3. Chỉ thêm từ thiếu — Thêm từ chưa có. Nếu từ gốc đã tồn tại, giữ nguyên nghĩa cũ không thay đổi.
                    </div>
                  </div>
                </label>
              </div>
            </div>
          )}

          {/* Status Message & Progress Bar */}
          {isProcessing && (
            <div style={{ marginBottom: '1rem' }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: '#eff6ff',
                  color: '#1d4ed8',
                  borderRadius: '0.375rem',
                  fontSize: '0.8125rem',
                  marginBottom: '0.5rem',
                }}
              >
                <Loader2 size={18} className="animate-spin" />
                <span style={{ fontWeight: 600 }}>{statusMessage}</span>
              </div>

              {progressPercent > 0 && (
                <div style={{ width: '100%', height: '8px', backgroundColor: '#e2e8f0', borderRadius: '4px', overflow: 'hidden' }}>
                  <div
                    style={{
                      height: '100%',
                      width: `${progressPercent}%`,
                      backgroundColor: 'var(--action-primary)',
                      transition: 'width 0.15s ease-out',
                    }}
                  />
                </div>
              )}
            </div>
          )}

          {/* Footer Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
            {isProcessing ? (
              <button
                type="button"
                onClick={handleCancelImport}
                className="btn-danger"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  fontSize: '0.875rem',
                }}
              >
                <StopCircle size={16} />
                <span>Hủy tiến trình</span>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={onClose}
                  style={{
                    padding: '0.5rem 1rem',
                    borderRadius: '0.375rem',
                    border: '1px solid var(--border-light)',
                    backgroundColor: '#ffffff',
                    color: 'var(--text-main)',
                    fontSize: '0.875rem',
                  }}
                >
                  Đóng
                </button>
                <button
                  type="submit"
                  disabled={!selectedFile}
                  className="btn-primary"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.375rem',
                    fontSize: '0.875rem',
                    opacity: !selectedFile ? 0.6 : 1,
                    cursor: !selectedFile ? 'not-allowed' : 'pointer',
                  }}
                >
                  <FileText size={16} />
                  <span>Bắt đầu nhập</span>
                </button>
              </>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};
